import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { InfoButton } from '../../components/InfoModal';

const EMPTY = {
  name: '',
  contact_name: '',
  email: '',
  phone: '',
  address: '',
  notes: '',
  is_active: true,
};

export default function PartlyAgentTab() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | agent
  const [form, setForm] = useState(EMPTY);

  const { data: agents = [], isLoading } = useQuery({
    queryKey: ['partly-agents', 'all'],
    queryFn: async () =>
      (await api.get('/partly-agents', { params: { includeInactive: 1 } })).data?.data || [],
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter((a) =>
      [a.name, a.contact_name, a.email, a.phone, a.address]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [agents, search]);

  const saveMutation = useMutation({
    mutationFn: async (body) => {
      if (editing && editing !== 'new' && editing.id) {
        return (await api.put(`/partly-agents/${editing.id}`, body)).data?.data;
      }
      return (await api.post('/partly-agents', body)).data?.data;
    },
    onSuccess: () => {
      toast.success(editing && editing !== 'new' ? 'Partly agent updated' : 'Partly agent added');
      setEditing(null);
      setForm(EMPTY);
      queryClient.invalidateQueries({ queryKey: ['partly-agents'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Save failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => api.delete(`/partly-agents/${id}`),
    onSuccess: () => {
      toast.success('Partly agent deleted');
      queryClient.invalidateQueries({ queryKey: ['partly-agents'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Delete failed'),
  });

  const openNew = () => {
    setForm(EMPTY);
    setEditing('new');
  };

  const openEdit = (a) => {
    setForm({
      name: a.name || '',
      contact_name: a.contact_name || '',
      email: a.email || '',
      phone: a.phone || '',
      address: a.address || '',
      notes: a.notes || '',
      is_active: a.is_active !== false,
    });
    setEditing(a);
  };

  const onSave = (e) => {
    e.preventDefault();
    if (!String(form.name || '').trim()) {
      toast.error('Company name is required');
      return;
    }
    saveMutation.mutate(form);
  };

  return (
    <div>
      <div className="flex flex-wrap items-baseline gap-2 mb-4">
        <p className="text-sm text-gray-600">
          External Agent companies for buildings whose inspection coverage is <strong>Partial</strong>.
          Choose one under Owner on Add/Edit Building, or register the company here. Paperwork hand-off
          uses this contact.
        </p>
        <InfoButton title="Partly agent">
          <p>
            When coverage is Partial, another company holds the rest of the BWOF. That External Agent
            is chosen under Owner on the building form. Register companies here, or enter a new company
            on the building and it is added to this list.
          </p>
        </InfoButton>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, contact, email, phone…"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <button
          type="button"
          onClick={openNew}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700"
        >
          <Plus className="w-4 h-4" />
          Add Partly agent
        </button>
      </div>

      {editing && (
        <form
          onSubmit={onSave}
          className="mb-4 p-4 rounded-lg border border-sky-200 bg-sky-50/80 space-y-3"
        >
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900">
              {editing === 'new' ? 'New Partly agent' : `Edit: ${editing.name}`}
            </p>
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setForm(EMPTY);
              }}
              className="p-1 text-gray-500 hover:text-gray-800"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block text-xs font-medium text-gray-600">
              Company name *
              <input
                required
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </label>
            <label className="block text-xs font-medium text-gray-600">
              Contact
              <input
                value={form.contact_name}
                onChange={(e) => setForm((p) => ({ ...p, contact_name: e.target.value }))}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </label>
            <label className="block text-xs font-medium text-gray-600">
              Email
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </label>
            <label className="block text-xs font-medium text-gray-600">
              Phone
              <input
                value={form.phone}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </label>
            <label className="block text-xs font-medium text-gray-600 md:col-span-2">
              Address
              <input
                value={form.address}
                onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </label>
            <label className="block text-xs font-medium text-gray-600 md:col-span-2">
              Notes
              <textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                checked={!!form.is_active}
                onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                className="h-4 w-4 rounded border-gray-300 text-primary-600"
              />
              Active
            </label>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saveMutation.isPending}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setForm(EMPTY);
              }}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="bg-card rounded-lg border border-gray-200 overflow-hidden">
        {isLoading ? (
          <p className="p-8 text-center text-gray-500">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="p-8 text-center text-gray-500">
            {search.trim()
              ? 'No matches.'
              : 'No Partly agents yet. Add one here, or type a new name on Add/Edit Building.'}
          </p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Company</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Status</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Contact</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Buildings</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((a) => (
                <tr key={a.id} className={a.is_active ? '' : 'bg-gray-50/80'}>
                  <td className="px-4 py-3 font-medium text-gray-900">{a.name}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        a.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {a.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {[a.contact_name, a.email, a.phone].filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{a.building_count ?? 0}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => openEdit(a)}
                      className="inline-flex p-1.5 text-gray-600 hover:text-primary-700"
                      title="Edit"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Delete “${a.name}”? Linked buildings will clear this Main agent.`
                          )
                        ) {
                          deleteMutation.mutate(a.id);
                        }
                      }}
                      className="inline-flex p-1.5 text-gray-600 hover:text-red-700"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
